/**
 * カレンダーイベントのクリック処理と詳細シート
 *
 * 日別ビューと連続スクロールビューで同じ挙動を共有する。
 */

import { useCallback, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import type { OverlandVisit } from '@/api/types/overland'
import type { MovementSessionWithVisits } from '@/lib/movement-session'
import { RECEIPT_RETURN_STATE } from '@/pages/receipts/receipt-query'
import { PATHS } from '@/routes/paths'
import { useOverlandStore } from '@/store/useOverlandStore'

import { MovementDetailSheet } from './activity-card/movement-detail-sheet'
import { PlaceDetailSheet } from './activity-card/place-detail-sheet'
import { PlaceEditSheet } from './activity-card/place-edit-sheet'
import type { CalendarEventProps } from './calendar-event-builder'

export const useCalendarSheets = () => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const visits = useOverlandStore((state) => state.visits)

  const [selectedVisit, setSelectedVisit] = useState<OverlandVisit | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedMovement, setSelectedMovement] = useState<MovementSessionWithVisits | null>(null)

  const handleEventSelect = useCallback(
    ({ type, visit, session, receiptId, workoutSessionId }: CalendarEventProps) => {
      if (type === 'visit' && visit) {
        setSelectedVisit(visit)
        if (visit.place) {
          setIsDetailOpen(true)
        } else {
          setIsEditOpen(true)
        }
      } else if (type === 'movement' && session) {
        setSelectedMovement(session)
      } else if (type === 'receipt' && receiptId !== undefined) {
        void navigate(`${PATHS.RECEIPTS.LIST}?id=${receiptId}`, { state: RECEIPT_RETURN_STATE })
      } else if (type === 'workout' && workoutSessionId !== undefined) {
        void navigate(PATHS.WORKOUT.SESSION.replace(':session_id', String(workoutSessionId)))
      } else if (type === 'todo') {
        void navigate(PATHS.TODO.HOME)
      } else if (type === 'sleep' || type === 'activity') {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.set('tab', 'health')
          return newParams
        })
      }
    },
    [setSearchParams, navigate],
  )

  // 編集シートを開く（詳細シートを閉じてから）
  const handleOpenEdit = useCallback(() => {
    setIsDetailOpen(false)
    setIsEditOpen(true)
  }, [])

  // ストア側が最新を持っていればそちらを優先する
  const latestVisit = selectedVisit ? (visits.find((v) => v.id === selectedVisit.id) ?? selectedVisit) : null

  const sheets = (
    <>
      {latestVisit && (
        <PlaceDetailSheet
          visit={latestVisit}
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          onEditClick={handleOpenEdit}
        />
      )}

      {latestVisit && <PlaceEditSheet visit={latestVisit} open={isEditOpen} onOpenChange={setIsEditOpen} />}

      {selectedMovement && (
        <MovementDetailSheet
          session={selectedMovement}
          open={!!selectedMovement}
          onOpenChange={(open) => !open && setSelectedMovement(null)}
        />
      )}
    </>
  )

  return { handleEventSelect, sheets }
}
